import type { ReactNode } from 'react'

import { styled } from 'styled-components'

import { Button, FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'

import { ModalFooter } from '@/components/common/modal/ui'

// Building blocks for a filter modal laid out in sections: a panel above the
// options (e.g. Lot composition), a section header with "Select all", and a
// one-row footer with the selection summary beside the actions.

export const ModalPanelArea = styled.div`
  padding: 0 3rem;

  @media ${Breakpoints.max.medium} {
    padding: 0 1.5rem;
  }
`

const SectionHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 3rem 0;

  @media ${Breakpoints.max.medium} {
    padding: 1.25rem 1.5rem 0;
  }
`

const SectionTitle = styled.h3`
  ${Typography.Subtitle1};
  color: ${designColors.neutral.black};
  margin: 0;
`

export const ModalSectionHeader = ({
  title,
  children,
}: {
  title: ReactNode
  children?: ReactNode
}) => (
  <SectionHeaderRow>
    <SectionTitle>{title}</SectionTitle>
    {children}
  </SectionHeaderRow>
)

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 3rem;
  border-top: 0.0625rem solid ${designColors.neutral.mediumGray};

  @media ${Breakpoints.max.medium} {
    flex-wrap: wrap;
    padding: 1rem 1.5rem;
  }
`

const Summary = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const SummaryText = styled.p`
  ${Typography.Subtitle2};
  color: ${designColors.neutral.black};
  margin: 0;
`

const SummaryNote = styled.p`
  ${Typography.Body4};
  color: ${designColors.neutral.veryDarkGray};
  margin: 0;
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const SubmitButton = styled(Button)`
  min-width: 10rem;
`

export const ModalSummaryFooter = ({
  summary,
  note,
  onCancel,
  onSubmit,
}: {
  summary: ReactNode
  note?: ReactNode
  onCancel: () => void
  onSubmit: () => void
}) => (
  <ModalFooter>
    <FooterRow>
      <Summary>
        <SummaryText data-testid="selected-count">{summary}</SummaryText>
        {note && <SummaryNote>{note}</SummaryNote>}
      </Summary>
      <Actions>
        <Button
          type="button"
          size="medium"
          appearance="tertiary"
          onClick={onCancel}
        >
          <FormattedMessage id="Common.cancel" />
        </Button>
        <SubmitButton type="button" size="medium" onClick={onSubmit}>
          <FormattedMessage id="Common.showResult" />
        </SubmitButton>
      </Actions>
    </FooterRow>
  </ModalFooter>
)
